import React, { useState, useEffect } from "react";
import axios from "axios";
import HeaderMain from "../../dasboard-component/Components/HeaderMain";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import ConfirmationModal from "../../common/ConfirmationModal";

const UserAffiliationsDashboard = () => {
  const [affiliationsData, setAffiliationsData] = useState({
    pendingRequests: [],
    affiliations: [],
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, active, left, rejected
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const { user } = useAuth();
  const userId = user?.uid;

  useEffect(() => {
    if (userId) {
      fetchUserAffiliations();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchUserAffiliations = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/user-affiliations`,
        { withCredentials: true }
      );
      setAffiliationsData(response.data);
    } catch (error) {
      console.error("Error fetching user affiliations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveInstitute = async (requestId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/leave/${requestId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Left institute successfully!");
      fetchUserAffiliations();
    } catch (error) {
      console.error("Error leaving institute:", error);
      toast.error("Error leaving institute");
    }
  };

  const handleCancelRequest = (affiliation) => {
    setSelectedAffiliation(affiliation);
    setModalAction("cancel");
    setShowConfirmModal(true);
  };

  const handleConfirmCancelRequest = async () => {
    if (!selectedAffiliation) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/institute/cancel-request/${selectedAffiliation.id}`,
        { withCredentials: true }
      );
      toast.success("Request cancelled successfully!");
      fetchUserAffiliations();
      setShowConfirmModal(false);
      setSelectedAffiliation(null);
      setModalAction(null);
    } catch (error) {
      console.error("Error cancelling request:", error);
      toast.error("Error cancelling request");
      setShowConfirmModal(false);
      setSelectedAffiliation(null);
      setModalAction(null);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setSelectedAffiliation(null);
    setModalAction(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        label: "Pending",
      },
      active: { bg: "bg-green-100", text: "text-green-800", label: "Active" },
      left: { bg: "bg-gray-100", text: "text-gray-800", label: "Left" },
      rejected: { bg: "bg-red-100", text: "text-red-800", label: "Rejected" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  // Combine pending requests and affiliations into one array for filtering
  const allItems = [
    ...affiliationsData.pendingRequests.map((req) => ({
      ...req,
      status: "pending",
      type: "request",
    })),
    ...affiliationsData.affiliations.map((aff) => ({
      ...aff,
      type: "affiliation",
    })),
  ];

  const filteredAffiliations = allItems.filter((item) => {
    if (filter === "all") return true;
    return item.status === filter;
  });

  if (loading) {
    return (
      <div>
        <HeaderMain />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading affiliations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div>
        <HeaderMain pageType="instituteDashboard" />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Please Login
            </h2>
            <p className="text-gray-600 mb-6">
              You need to be logged in to view your affiliations.
            </p>
            <a
              href="/login-arena"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderMain pageType="instituteDashboard" />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              My Institute Affiliations
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your connections with sports institutes
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: "All" },
                { key: "pending", label: "Pending" },
                { key: "active", label: "Active" },
                { key: "left", label: "Left" },
                { key: "rejected", label: "Rejected" },
              ].map((filterOption) => (
                <button
                  key={filterOption.key}
                  onClick={() => setFilter(filterOption.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    filter === filterOption.key
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {filterOption.label} (
                  {
                    allItems.filter(
                      (a) =>
                        filterOption.key === "all" ||
                        a.status === filterOption.key
                    ).length
                  }
                  )
                </button>
              ))}
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Requests
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {affiliationsData.affiliations.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Active
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {allItems.filter((a) => a.status === "active").length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Pending
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {allItems.filter((a) => a.status === "pending").length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Rejected
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {allItems.filter((a) => a.status === "rejected").length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Affiliations List */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                {filter === "all"
                  ? "All Affiliations"
                  : `${
                      filter.charAt(0).toUpperCase() + filter.slice(1)
                    } Affiliations`}
              </h2>
            </div>

            {filteredAffiliations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No affiliations found for the selected filter
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredAffiliations.map((affiliation) => (
                  <div key={affiliation.id} className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1 w-full">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                              <span className="text-lg font-medium text-gray-600">
                                {affiliation.institute_name
                                  ?.charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-lg font-medium text-gray-900">
                              {affiliation.institute_name ||
                                "Unknown Institute"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {affiliation.institute_email}
                            </div>
                            {affiliation.verification_id && (
                              <div className="text-sm text-blue-600 font-medium flex items-center gap-1 mt-1">
                                <span>🔑</span>
                                ID: {affiliation.verification_id}
                              </div>
                            )}
                            <div className="flex items-center space-x-4 mt-2">
                              <div className="text-xs text-gray-400">
                                {affiliation.type === "request"
                                  ? "Requested"
                                  : "Joined"}
                                :{" "}
                                {new Date(
                                  affiliation.type === "request"
                                    ? affiliation.created_at
                                    : affiliation.joined_at
                                ).toLocaleDateString()}
                              </div>
                              {affiliation.left_at && (
                                <div className="text-xs text-gray-400">
                                  Left:{" "}
                                  {new Date(
                                    affiliation.left_at
                                  ).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 w-full md:w-auto">
                        {getStatusBadge(affiliation.status)}

                        {affiliation.status === "active" && (
                          <button
                            onClick={() => handleLeaveInstitute(affiliation.id)}
                            className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 transition-colors w-full sm:w-auto"
                          >
                            Leave Institute
                          </button>
                        )}

                        {affiliation.status === "pending" &&
                          affiliation.type === "request" && (
                            <button
                              onClick={() => handleCancelRequest(affiliation)}
                              className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-700 transition-colors w-full sm:w-auto"
                            >
                              Cancel Request
                            </button>
                          )}

                        {affiliation.status === "pending" &&
                          affiliation.type !== "request" && (
                            <span className="text-sm text-gray-500 italic">
                              Waiting for approval
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-8 text-center">
            <a
              href="/institute-dashboard"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Find More Institutes
            </a>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Cancel Request */}
      <ConfirmationModal
        isOpen={showConfirmModal && modalAction === "cancel"}
        title="Cancel Request"
        message={`Are you sure you want to cancel your affiliation request to ${selectedAffiliation?.institute_name}?`}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCloseModal}
        confirmText="Yes, Cancel"
        cancelText="Keep Request"
        confirmButtonClass="bg-red-600 text-white hover:bg-red-700"
      />
    </div>
  );
};

export default UserAffiliationsDashboard;
